import { camelize } from 'frontend-utils/utils/convert-case';
import {
  INVALID_RESERVATIONS_MESSAGE,
  KPIS,
  SUMMABLE_METRIC_KEYS,
} from 'lumen/utils/constants/ri-constant-values';
import safeDivide from 'lumen/utils/numeric/safe-divide';

export default class SegmentationObjectValue {
  static EMPTY_SEGMENTATION_OBJECT_VALUE = Object.freeze({
    [KPIS.ADR]: null,
    [KPIS.NR_OF_ROOMS]: null,
    [KPIS.REVENUE]: null,
    [KPIS.OCCUPANCY]: null,
    [KPIS.CAPACITY]: null,
  });

  constructor(segmentationAPIObjectValue) {
    Object.keys(segmentationAPIObjectValue).forEach((key) => {
      this[camelize(key)] = segmentationAPIObjectValue[key];
    });
  }

  static calculateAndAssignOccupancy(value, capacity = value?.capacity) {
    const nrOfRoomsKeyList = Object.keys(value).filter((key) =>
      key.includes(KPIS.NR_OF_ROOMS),
    );

    if (value.message === INVALID_RESERVATIONS_MESSAGE) {
      nrOfRoomsKeyList.forEach((key) => {
        const occupancyKey = key.replace(KPIS.NR_OF_ROOMS, KPIS.OCCUPANCY);
        value[occupancyKey] = null;
      });

      return;
    }

    nrOfRoomsKeyList.forEach((key) => {
      const occupancy = (value[key] / capacity) * 100;
      const occupancyKey = key.replace(KPIS.NR_OF_ROOMS, KPIS.OCCUPANCY);

      const finiteOccupancyOrNull = Number.isFinite(occupancy)
        ? occupancy
        : null;

      value[occupancyKey] = finiteOccupancyOrNull;
    });
  }

  static calculateAndAssignRevpar(value, capacity = value?.capacity) {
    const revenueKeyList = Object.keys(value).filter((key) =>
      key.includes(KPIS.REVENUE),
    );

    if (value.message === INVALID_RESERVATIONS_MESSAGE) {
      revenueKeyList.forEach((key) => {
        const revparKey = key.replace(KPIS.REVENUE, KPIS.REVPAR);
        value[revparKey] = null;
      });

      return;
    }

    revenueKeyList.forEach((key) => {
      const revpar = value[key] / capacity;
      const revparKey = key.replace(KPIS.REVENUE, KPIS.REVPAR);

      const finiteRevparOrNull = Number.isFinite(revpar) ? revpar : null;

      value[revparKey] = finiteRevparOrNull;
    });
  }

  static calculateCancellationPercentage(value, referenceValue) {
    if (!value || !referenceValue) {
      return SegmentationObjectValue.EMPTY_SEGMENTATION_OBJECT_VALUE;
    }

    return Object.keys(value).reduce((cancellationPercentage, key) => {
      if (value[key] === null || referenceValue[key] === null) {
        cancellationPercentage[key] = null;
        return cancellationPercentage;
      }

      const numerator = referenceValue[key];
      const denominator = value[key] + referenceValue[key];

      if (denominator === 0) {
        cancellationPercentage[key] = null;
      } else {
        cancellationPercentage[key] = safeDivide(numerator, denominator) * 100;
      }

      return cancellationPercentage;
    }, {});
  }

  static calculateChange(value, referenceValue) {
    if (
      !value ||
      !referenceValue ||
      value.message === INVALID_RESERVATIONS_MESSAGE ||
      referenceValue.message === INVALID_RESERVATIONS_MESSAGE
    ) {
      return SegmentationObjectValue.EMPTY_SEGMENTATION_OBJECT_VALUE;
    }

    return Object.keys(value).reduce((change, key) => {
      if (value[key] === null || referenceValue[key] === null) {
        change[key] = null;
        return change;
      }

      if (key.includes(KPIS.OCCUPANCY) || key.includes(KPIS.REVPAR)) {
        const ptChange = value[key] - referenceValue[key];

        const finitePtChangeOrNull = Number.isFinite(ptChange)
          ? Math.round(ptChange)
          : null;

        change[key] = finitePtChangeOrNull;
        return change;
      }

      change[key] = value[key] - referenceValue[key];
      return change;
    }, {});
  }

  static calculateRelativeChange(value, referenceValue) {
    if (
      !value ||
      !referenceValue ||
      value.message === INVALID_RESERVATIONS_MESSAGE ||
      referenceValue.message === INVALID_RESERVATIONS_MESSAGE
    ) {
      return SegmentationObjectValue.EMPTY_SEGMENTATION_OBJECT_VALUE;
    }

    return Object.keys(value).reduce((relativeChange, key) => {
      if (value[key] === null || referenceValue[key] === null) {
        relativeChange[key] = null;
        return relativeChange;
      }

      const numerator = value[key] - referenceValue[key];
      const denominator = Math.abs(referenceValue[key]);

      if (denominator === 0) {
        relativeChange[key] = null;
      }

      relativeChange[key] = safeDivide(numerator, denominator) * 100;

      return relativeChange;
    }, {});
  }

  static calculateReservationsDiffPercentage(value, referenceValue) {
    if (!value || !referenceValue) {
      return SegmentationObjectValue.EMPTY_SEGMENTATION_OBJECT_VALUE;
    }

    return Object.keys(value).reduce((reservationsDiffPercentage, key) => {
      const numerator = value[key];
      const denominator = referenceValue[key];
      reservationsDiffPercentage[key] =
        safeDivide(numerator, denominator) * 100;
      return reservationsDiffPercentage;
    }, {});
  }

  static calculateSum(value, referenceValue) {
    if (!value || !referenceValue) {
      return SegmentationObjectValue.EMPTY_SEGMENTATION_OBJECT_VALUE;
    }

    const sum = {};

    SUMMABLE_METRIC_KEYS.forEach((key) => {
      if (Number.isFinite(value[key]) && Number.isFinite(referenceValue[key])) {
        sum[key] = value[key] + referenceValue[key];
      } else if (Number.isFinite(value[key])) {
        sum[key] = value[key];
      } else if (Number.isFinite(referenceValue[key])) {
        sum[key] = referenceValue[key];
      } else {
        sum[key] = null;
      }
    });

    Object.keys(sum)
      .filter((key) => key.includes(KPIS.REVENUE))
      .forEach((revenueKey) => {
        const adrKey = revenueKey.replace(KPIS.REVENUE, KPIS.ADR);
        const nrOfRoomsKey = revenueKey.replace(KPIS.REVENUE, KPIS.NR_OF_ROOMS);

        sum[adrKey] = safeDivide(sum[revenueKey], sum[nrOfRoomsKey]);
      });

    return sum;
  }

  static createFromAPI(segmentationAPIObjectValue) {
    if (!segmentationAPIObjectValue) {
      return null;
    }

    return new SegmentationObjectValue(segmentationAPIObjectValue);
  }
}
